import {
    type ChangeEventHandler,
    type FocusEventHandler,
    type HTMLAttributes,
    type KeyboardEventHandler,
    type ReactNode,
    type RefObject,
    useEffect,
} from 'react';

import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ClassValue } from 'clsx';
import { clsx } from 'clsx';

import { cn } from '../../lib/utils';
import { FieldDescription } from '../FieldDescription';
import { FieldError } from '../FieldError';
import { FieldLabel } from '../FieldLabel';
import { Loader } from '../Loader';

export interface TextFieldProps {
    type?: 'email' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url' | 'time';
    id?: string;
    name?: string;
    label?: ReactNode; // Preferably a string for keeping styles consistent
    value?: string;
    defaultValue?: string;
    placeholder?: string;
    description?: string;
    dataTestid?: string;
    labelHtmlAttributes?: HTMLAttributes<HTMLLabelElement>;
    inputHtmlAttributes?: HTMLAttributes<HTMLInputElement>;
    className?: ClassValue;
    inputClassName?: ClassValue;
    required?: boolean;
    disabled?: boolean;
    prefix?: ReactNode;
    suffix?: ReactNode;
    isFetching?: boolean;
    errorMessage?: string;
    noOutline?: boolean;
    inputRef?: RefObject<HTMLInputElement>;
    size?: 'normal' | 'small';
    focusOnMount?: boolean; // Mus have focusOnMount and inputRef together to work, otherwise it would throw an error if inputRef is undefined
    icon?: IconDefinition;
    onChange: ChangeEventHandler<HTMLInputElement>;
    onFocus?: FocusEventHandler<HTMLInputElement>;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
}

export const TextField = ({
    id,
    name,
    type = 'text',
    label = '',
    defaultValue = '',
    value,
    placeholder = '',
    description = '',
    dataTestid,
    labelHtmlAttributes,
    inputHtmlAttributes,
    className,
    inputClassName,
    required = false,
    disabled = false,
    prefix = '',
    suffix = '',
    isFetching = false,
    errorMessage = '',
    noOutline = false,
    inputRef,
    size = 'normal',
    focusOnMount = false,
    icon,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
}: TextFieldProps) => {
    const isControlled = value !== undefined;

    useEffect(() => {
        if (focusOnMount && inputRef?.current) {
            inputRef.current.focus();
        }
    }, [focusOnMount, inputRef]);

    return (
        <div
            className={clsx(['min-w-16', className])}
            data-component-name='TextField'
        >
            {/* min-w-16 because it needs a min width for shrinking in a flex container */}
            {label && (
                <FieldLabel
                    label={
                        <>
                            {label} {required && '*'}
                        </>
                    }
                    htmlFor={id || name}
                    className={clsx([
                        'mb-2 block truncate text-xs font-medium leading-4',
                        disabled ? 'text-gray-400' : 'text-gray-800',
                        { 'ml-9': icon },
                    ])}
                    dataTestid={dataTestid && `${dataTestid}-label`}
                    htmlAttributes={labelHtmlAttributes}
                />
            )}
            <div className='flex items-center gap-3'>
                {icon && (
                    <FontAwesomeIcon
                        className='w-6 py-2.5 shrink-0 text-gray-500'
                        icon={icon}
                    />
                )}
                <div
                    className={cn([
                        `flex items-center flex-1 min-w-0 shrink`,
                        noOutline
                            ? 'ring-0'
                            : 'rounded-md ring-1 ring-inset focus-within:ring-2 focus-within:ring-inset',
                        {
                            'bg-gray-100': disabled,
                            'bg-white': !disabled,
                            'ring-red-300 focus-within:ring-red-300': errorMessage,
                            'ring-gray-300 focus-within:ring-blue-300': !errorMessage,
                        },
                        inputClassName,
                    ])}
                >
                    {prefix && (
                        <span
                            className='flex select-none items-center pl-2.5 font-normal text-gray-500 sm:text-sm'
                            data-testid={dataTestid && `${dataTestid}-prefix`}
                        >
                            {prefix}
                        </span>
                    )}
                    <input
                        type={type}
                        id={id || name}
                        name={name}
                        className={cn([
                            // Common styles
                            'block min-w-0 grow shrink border-0 bg-transparent pl-2.5 font-normal text-gray-800 placeholder:text-gray-500 focus:ring-0 placeholder:truncate',
                            // Size variants
                            size === 'normal' && 'py-2 sm:text-sm sm:leading-6',
                            size === 'small' && 'py-1 text-xs leading-5',
                            // Disable styles
                            'disabled:cursor-not-allowed disabled:text-gray-400 disabled:placeholder:text-gray-400',
                        ])}
                        placeholder={placeholder}
                        disabled={disabled}
                        size={1} // Allow input to shrink (as min-w-0 above) : https://stackoverflow.com/a/29990524
                        data-testid={dataTestid && `${dataTestid}-input`}
                        {...(isControlled ? { value } : { defaultValue })}
                        ref={inputRef}
                        onChange={onChange}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onKeyDown={onKeyDown}
                        {...inputHtmlAttributes}
                        onWheel={(e) => e.currentTarget.blur()}
                    />
                    {isFetching && (
                        <div className='pr-2.5'>
                            <Loader className='w-3' />
                        </div>
                    )}
                    {suffix && (
                        <span
                            className='flex select-none items-center pr-2.5 font-normal text-gray-500 sm:text-sm'
                            data-testid={`${dataTestid}-suffix`}
                        >
                            {suffix}
                        </span>
                    )}
                </div>
            </div>
            {description && <FieldDescription dataTestid={`${dataTestid}-description`}>{description}</FieldDescription>}
            {errorMessage && <FieldError dataTestid={`${dataTestid}-error`}>{errorMessage}</FieldError>}
        </div>
    );
};
